import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { sttServerTranscribe } from "@/lib/api";

/**
 * Tap-to-speak voice commander. Records ~3 s, sends to the configured
 * server STT, fuzzy-matches the transcript against the intent map and
 * executes the action (navigate, click submit, click cancel, etc.).
 *
 * Server-only (browser Web Speech is NOT used here) — the same engine
 * the dictation textareas use, so one config controls both.
 */

type Intent = {
  /** Patterns to match in the transcript. Marathi + Hindi + English variants. */
  match: (string | RegExp)[];
  label: string;          // human-readable name shown in the toast
  action: (ctx: ActionCtx) => void;
};

interface ActionCtx {
  navigate: ReturnType<typeof useNavigate>;
}

const INTENTS: Intent[] = [
  // Navigation — Operations
  { match: ["dashboard", "डॅशबोर्ड", "मुख्य"],
    label: "Dashboard", action: ({navigate}) => navigate("/dashboard") },
  { match: ["station diary", "ठाणे डायरी", "जीडी", "general diary"],
    label: "Station Diary", action: ({navigate}) => navigate("/station-diary") },
  { match: ["complaint", "तक्रार", "complaints"],
    label: "Complaints", action: ({navigate}) => navigate("/complaints") },
  { match: ["new complaint", "नवीन तक्रार"],
    label: "+ New Complaint", action: ({navigate}) => navigate("/complaints/new") },
  { match: ["fir list", "एफआयआर", "एफ आय आर", "fir"],
    label: "FIR list", action: ({navigate}) => navigate("/firs") },
  { match: ["new fir", "नवीन एफआयआर", "draft fir"],
    label: "+ New FIR", action: ({navigate}) => navigate("/fir/new") },
  { match: ["arrest", "अटक"],
    label: "Arrests", action: ({navigate}) => navigate("/arrests") },
  { match: ["charge sheet", "चार्जशीट", "आरोपपत्र", "charge-sheet"],
    label: "Charge Sheets", action: ({navigate}) => navigate("/charge-sheets") },
  // Investigation
  { match: ["panchanama", "पंचनामा"],
    label: "Panchanamas", action: ({navigate}) => navigate("/panchanamas") },
  { match: ["mlc", "medical", "वैद्यकीय"],
    label: "MLC", action: ({navigate}) => navigate("/medico-legal") },
  { match: ["fsl", "forensic", "न्यायवैद्यक"],
    label: "FSL", action: ({navigate}) => navigate("/forensic") },
  { match: ["malkhana", "मालखाना", "evidence store"],
    label: "Malkhana", action: ({navigate}) => navigate("/malkhana") },
  { match: ["court", "कोर्ट", "न्यायालय"],
    label: "Court Filings", action: ({navigate}) => navigate("/court/filings") },
  { match: ["summons", "warrant", "वारंट", "समन्स"],
    label: "Summons / Warrants", action: ({navigate}) => navigate("/process-docs") },
  { match: ["bail", "जामीन"],
    label: "Bail", action: ({navigate}) => navigate("/bail") },
  // Registers
  { match: ["missing", "हरवलेली", "हरवलेल्या", "missing person"],
    label: "Missing Persons", action: ({navigate}) => navigate("/missing-persons") },
  { match: ["lost found", "lost and found", "हरवलेल्या वस्तू", "सापडलेल्या"],
    label: "Lost & Found", action: ({navigate}) => navigate("/lost-found") },
  { match: ["wanted", "absconder", "वॉन्टेड", "फरारी"],
    label: "Wanted / Absconders", action: ({navigate}) => navigate("/wanted") },
  { match: ["verification", "passport", "पडताळणी", "पासपोर्ट", "character"],
    label: "Verification", action: ({navigate}) => navigate("/verification") },
  // Officer
  { match: ["my profile", "profile", "माझी प्रोफाइल", "मी"],
    label: "Profile", action: ({navigate}) => navigate("/me") },
  { match: ["search", "शोधा", "शोध"],
    label: "Search", action: ({navigate}) => navigate("/search") },
  { match: ["report", "अहवाल", "reports"],
    label: "Reports", action: ({navigate}) => navigate("/reports") },
  { match: ["help", "मदत"],
    label: "Help", action: ({navigate}) => navigate("/help") },
  { match: ["settings", "सेटिंग्ज", "सेटिंग"],
    label: "Settings", action: ({navigate}) => navigate("/settings") },

  // Form actions — DOM-based, work on whatever page is active
  { match: ["save", "submit", "सेव्ह करा", "जतन करा", "जतन"],
    label: "Save (click submit)", action: () => clickFirstSubmit() },
  { match: ["cancel", "रद्द करा", "बंद करा", "close"],
    label: "Cancel / Close",
    action: () => {
      // close dialog (Esc) or navigate back
      document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
      const cancelBtn = Array.from(document.querySelectorAll<HTMLButtonElement>("button"))
        .find(b => /cancel|रद्द|बंद/i.test(b.textContent ?? ""));
      cancelBtn?.click();
    } },
  { match: ["back", "मागे जा", "मागे", "पाछे"],
    label: "Back", action: () => window.history.back() },
  { match: ["forward", "next", "पुढे जा", "पुढे"],
    label: "Forward / Next",
    action: () => {
      // Either browser-forward or click a "Next" button on the page
      const nextBtn = Array.from(document.querySelectorAll<HTMLButtonElement>("button"))
        .find(b => /next|continue|पुढे/i.test(b.textContent ?? ""));
      if (nextBtn) nextBtn.click(); else window.history.forward();
    } },
];

function clickFirstSubmit() {
  // Prefer an explicit submit button inside an open <form>; else the most
  // recently rendered primary button (bg-mhpol-900).
  const formSubmit = document.querySelector<HTMLButtonElement>("form button[type='submit']");
  if (formSubmit) { formSubmit.click(); return; }
  const primary = Array.from(document.querySelectorAll<HTMLButtonElement>("button"))
    .filter(b => b.classList.contains("bg-mhpol-900") && !b.disabled)
    .pop();
  primary?.click();
}

function matchIntent(transcript: string): Intent | null {
  const t = transcript.trim().toLowerCase();
  if (!t) return null;
  for (const intent of INTENTS) {
    for (const m of intent.match) {
      if (typeof m === "string") {
        if (t.includes(m.toLowerCase())) return intent;
      } else if (m.test(t)) return intent;
    }
  }
  return null;
}

/** Public read-only view of the intent map for the AI Config tester. */
export function listVoiceIntents(): { label: string; phrases: string[] }[] {
  return INTENTS.map((it) => ({
    label: it.label,
    phrases: it.match.map((m) => (typeof m === "string" ? m : m.toString())),
  }));
}

/** Public matcher used by the AI Config tester to dry-run an intent. */
export function dryRunIntent(transcript: string): { matched: boolean; label?: string } {
  const i = matchIntent(transcript);
  return i ? { matched: true, label: i.label } : { matched: false };
}

export function VoiceCommander() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [toast, setToast] = useState<{ kind: "ok" | "miss" | "err"; text: string } | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss the toast after 4 s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function startListening() {
    if (busy || recording) return;
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setToast({ kind: "err", text: "Mic needs HTTPS or http://localhost" });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 2000) {
          setToast({ kind: "err", text: "No speech detected — hold longer" });
          return;
        }
        setBusy(true);
        try {
          const r = await sttServerTranscribe(blob, "mr");
          const text = (r.text || "").trim();
          if (!text) { setToast({ kind: "miss", text: "didn't catch that" }); return; }
          const intent = matchIntent(text);
          if (intent) {
            setToast({ kind: "ok", text: `“${text}” → ${intent.label}` });
            try { intent.action({ navigate }); } catch (e: any) {
              setToast({ kind: "err", text: `action failed: ${e?.message ?? e}` });
            }
          } else {
            setToast({ kind: "miss", text: `Heard: "${text}" — no matching command` });
          }
        } catch (e: any) {
          setToast({ kind: "err", text: `STT failed: ${e?.message ?? e}` });
        } finally { setBusy(false); }
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
      autoStopRef.current = setTimeout(() => { mr.stop(); setRecording(false); }, 3000);
    } catch (e: any) {
      setToast({ kind: "err", text: `Mic denied: ${e?.message ?? e}` });
    }
  }

  function stopListening() {
    recRef.current?.stop();
    setRecording(false);
  }

  return (
    <>
      <button
        type="button"
        onClick={recording ? stopListening : startListening}
        title="Voice command (server STT) — try: 'ठाणे डायरी', 'सेव्ह करा', 'मागे जा'"
        aria-label={recording ? "Stop voice command" : "Voice command"}
        className={`inline-flex shrink-0 items-center gap-1.5 rounded border border-white/20 px-2.5 py-1.5 text-sm transition-colors ${
          recording
            ? "bg-red-600 text-white animate-pulse"
            : busy
              ? "bg-mhpol-700 text-white opacity-60"
              : "bg-mhpol-700 text-white hover:bg-mhpol-600"
        }`}
      >
        <span aria-hidden="true">{busy ? "…" : recording ? "●" : "🎙"}</span>
        <span className="hidden sm:inline">{busy ? "thinking" : recording ? "listening (3s)" : "command"}</span>
      </button>

      {toast && (
        <div
          role="status"
          className={`fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 rounded-lg px-4 py-2 text-sm shadow-lg ${
            toast.kind === "ok" ? "bg-emerald-600 text-white" :
            toast.kind === "miss" ? "bg-amber-500 text-white" :
            "bg-red-600 text-white"
          }`}
        >
          {toast.text}
        </div>
      )}
    </>
  );
}
